// manufacturing/ProductionEntry.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
import Select from 'react-select';
import {
    Plus, Search, RotateCcw, Eye, Edit, Trash2, ChevronUp,
    Settings, Calendar, Package, DollarSign, AlertCircle, CheckCircle, TrendingUp
} from 'feather-icons-react/build/IconComponents';
import { manufacturingActions } from "../../core/action/manufacturingAction";
import { productAPI } from "../../core/action/api";

const ProductionEntry = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State management
    const [productions, setProductions] = useState([]);
    const [products, setProducts] = useState([]);                    // for Finished Goods dropdown (inventory products)
    const [rawMaterials, setRawMaterials] = useState([]);           // for Raw Materials tab (has stock)
    //const [finishedGoodsMaterials, setFinishedGoodsMaterials] = useState([]); // RM master — used for selling_price lookup only
    const [loading, setLoading] = useState(false);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedProduction, setSelectedProduction] = useState(null);

    // Form states
    const [formData, setFormData] = useState({
        production_date: new Date().toISOString().split('T')[0],
        batch_no: manufacturingActions.generateBatchNo(),
        notes: '',
        raw_materials: [],
        finished_goods: [],
        wastage: []
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState('raw_materials');
    const [globalErrors, setGlobalErrors] = useState([]);

    // ── Additional cost fields ──────────────────────────────────────────
    const [additionalCosts, setAdditionalCosts] = useState({
        labour_cost: 0,
        electricity_cost: 0,
        packing_cost: 0,
        transport_cost: 0
    });
    // ───────────────────────────────────────────────────────────────────

    // Profit margin (default 30%)
    const [profitMarginPercent, setProfitMarginPercent] = useState(30);
    // Tracks the selling_price of the raw material selected in Raw Materials tab
    const [rmSellingPrice, setRmSellingPrice] = useState(0);

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        from_date: '',
        to_date: ''
    });

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    const statusOptions = [
        { value: 'draft', label: 'Draft' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    const canCreate = manufacturingActions.hasManufacturingPermission('create-production');
    const canView = manufacturingActions.hasManufacturingPermission('view-production');
    const canEdit = manufacturingActions.hasManufacturingPermission('edit-production');

    useEffect(() => {
        loadProductions();
        loadProducts();
        loadRawMaterials();
        //loadFinishedGoodsMaterials(); // loads RM master for selling_price lookup
    }, []);

    // ── Load functions ──────────────────────────────────────────────────

    const loadProductions = async (page = pagination.page) => {
        if (!canView) { toast.error('You do not have permission to view productions'); return; }
        try {
            setLoading(true);
            const params = { page, limit: pagination.limit, search: filters.search, status: filters.status, from_date: filters.from_date, to_date: filters.to_date };
            const result = await manufacturingActions.getProductions(params);
            if (result.success) {
                setProductions(result.data || []);
                setPagination({ page: result.page || page, limit: pagination.limit, total: result.total || 0, totalPages: result.totalPages || 1 });
            } else {
                toast.error(result.message || 'Failed to load productions');
            }
        } catch (error) {
            console.error('Error loading productions:', error);
            toast.error('Failed to load productions');
        } finally {
            setLoading(false);
        }
    };

    // Products from inventory (used in Finished Goods dropdown)
    const loadProducts = useCallback(async () => {
        try {
            setLoadingProducts(true);
            const response = await productAPI.list({ limit: 1000, status: 'active' });
            let list = [];
            if (Array.isArray(response?.data)) list = response.data;
            else if (Array.isArray(response?.data?.products)) list = response.data.products;
            else if (Array.isArray(response?.products)) list = response.products;
            setProducts(list);
            if (list.length === 0) toast.warning('No active products found. Please add products first.');
        } catch (error) {
            console.error('Error loading products:', error);
            setProducts([]);
        } finally {
            setLoadingProducts(false);
        }
    }, []);

    // Raw materials with stock (used in Raw Materials input tab)
    const loadRawMaterials = useCallback(async () => {
        try {
            const response = await manufacturingActions.getRawMaterials({ limit: 1000, status: 'active', has_stock: true });
            if (response.success) setRawMaterials(response.data || []);
            else setRawMaterials([]);
        } catch (error) {
            console.error('Error loading raw materials:', error);
            setRawMaterials([]);
        }
    }, []);

    // All active raw materials — NOT shown in dropdown; only used for selling_price lookup
    // const loadFinishedGoodsMaterials = useCallback(async () => {
    //     try {
    //         const response = await manufacturingActions.getRawMaterials({ limit: 1000, status: 'active' });
    //         if (response.success) setFinishedGoodsMaterials(response.data || []);
    //         else setFinishedGoodsMaterials([]);
    //     } catch (error) {
    //         console.error('Error loading RM selling prices:', error);
    //         setFinishedGoodsMaterials([]);
    //     }
    // }, []);

    // ── Form helpers ────────────────────────────────────────────────────

    const generateNewBatchNo = () => setFormData(prev => ({ ...prev, batch_no: manufacturingActions.generateBatchNo() }));

    const resetForm = () => {
        setFormData({ production_date: new Date().toISOString().split('T')[0], batch_no: manufacturingActions.generateBatchNo(), notes: '', raw_materials: [], finished_goods: [], wastage: [] });
        setAdditionalCosts({ labour_cost: 0, electricity_cost: 0, packing_cost: 0, transport_cost: 0 });
        setErrors({});
        setGlobalErrors([]);
        setActiveTab('raw_materials');
        setProfitMarginPercent(30);
        setSubmitting(false);
    };

    const handleCloseModal = () => { resetForm(); setShowCreateModal(false); };


    // ── Additional cost handler ─────────────────────────────────────────
    const handleAdditionalCostChange = (field, value) => {
        setAdditionalCosts(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
    };
    // ───────────────────────────────────────────────────────────────────

    // ── Item management ─────────────────────────────────────────────────

    const handleAddRawMaterial = () => {
        setFormData(prev => ({ ...prev, raw_materials: [...prev.raw_materials, { id: Date.now(), material_id: '', product_name: '', sku: '', quantity_used: 0, unit: 'PCS', unit_cost: 0, selling_price: 0, total_cost: 0, remarks: '' }] }));
        setGlobalErrors([]);
    };

    const handleAddFinishedGood = () => {
        setFormData(prev => ({ ...prev, finished_goods: [...prev.finished_goods, { id: Date.now(), product_id: '', product_name: '', sku: '', quantity_produced: 0, unit: 'PCS', selling_price: 0, unit_cost: 0, total_cost: 0, remarks: '' }] }));
        setGlobalErrors([]);
    };

    const handleAddWastage = () => {
        setFormData(prev => ({ ...prev, wastage: [...prev.wastage, { id: Date.now(), product_id: '', product_name: '', sku: '', wastage_quantity: 0, unit: 'PCS', wastage_type: 'raw_material', reason: '', wastage_cost: 0, remarks: '' }] }));
        setGlobalErrors([]);
    };

    const handleRemoveItem = (type, index) => {
        setFormData(prev => ({ ...prev, [type]: prev[type].filter((_, i) => i !== index) }));
        setTimeout(() => recalculateFinishedGoods(), 100);
    };

    // ── CORE COST CALCULATIONS ──────────────────────────────────────────

    // Raw material total only
    const calcRawMaterialCost = (rawOverride) => {
        const items = rawOverride || formData.raw_materials;
        return items.reduce((sum, item) => sum + (item.total_cost || 0), 0);
    };

    // Labour + Electricity + Packing + Transport
    const calcAdditionalTotal = (additionalOverride) => {
        const costs = additionalOverride || additionalCosts;
        return (parseFloat(costs.labour_cost) || 0)
            + (parseFloat(costs.electricity_cost) || 0)
            + (parseFloat(costs.packing_cost) || 0)
            + (parseFloat(costs.transport_cost) || 0);
    };

    // Grand total cost = raw + all overheads
    const calcTotalProductionCost = (rawOverride, additionalOverride) => {
        return calcRawMaterialCost(rawOverride) + calcAdditionalTotal(additionalOverride);
    };

    const calcTotalFinishedQty = (finishedOverride) => {
        const items = finishedOverride || formData.finished_goods;
        return items.reduce((sum, item) => sum + (item.quantity_produced || 0), 0);
    };

    const calcFinishedGoodsValue = () =>
        formData.finished_goods.reduce((sum, item) => sum + (item.total_cost || 0), 0);

    // Recalculate finished goods unit cost whenever raw material costs change.
    // Selling price is preserved from raw material entry; total_cost = selling_price × qty.
    const recalculateFinishedGoods = (rawOverride, additionalOverride) => {
        const totalCost = calcTotalProductionCost(rawOverride, additionalOverride);
        const updatedFG = [...formData.finished_goods];
        const totalQty = updatedFG.reduce((sum, item) => sum + (item.quantity_produced || 0), 0);

        if (totalQty > 0 && totalCost > 0) {
            const costPerUnit = totalCost / totalQty;
            updatedFG.forEach((item, idx) => {
                updatedFG[idx].unit_cost = costPerUnit;
                // Keep selling_price from raw material; recalc total_cost
                updatedFG[idx].total_cost = (item.selling_price || 0) * (item.quantity_produced || 0);
            });
            setFormData(prev => ({ ...prev, finished_goods: updatedFG }));
        }
    };

    // ── Field change handlers ───────────────────────────────────────────

    const handleRawMaterialSelect = (index, selectedOption) => {
        const material = rawMaterials.find(m => m.id === selectedOption.value);
        if (material) {
            const updatedItems = [...formData.raw_materials];
            const qty = updatedItems[index].quantity_used || 0;
            // Store selling_price from raw material entry along with cost_price
            updatedItems[index] = {
                ...updatedItems[index],
                material_id: material.id,
                product_name: material.material_name,
                sku: material.sku,
                unit: material.unit_name || 'PCS',
                unit_cost: material.cost_price || 0,
                selling_price: parseFloat(material.selling_price) || 0,
                total_cost: (material.cost_price || 0) * qty
            };

            // Track the RM selling_price in a dedicated state for reliable access
            const spFromRM = parseFloat(material.selling_price) || 0;
            setRmSellingPrice(spFromRM);

            // Update all finished goods selling_price to match this raw material's selling_price
            setFormData(prev => ({
                ...prev,
                raw_materials: updatedItems,
                finished_goods: prev.finished_goods.map(fg => ({
                    ...fg,
                    selling_price: spFromRM,
                    total_cost: spFromRM * (fg.quantity_produced || 0)
                }))
            }));

            recalculateFinishedGoods(updatedItems);
        }
    };

    const handleFinishedGoodSelect = (index, selectedOption) => {
        // Product comes from inventory list (productAPI)
        const product = products.find(p => p.id === selectedOption.value);
        if (product) {
            const totalCost = calcTotalProductionCost();
            const updatedFG = [...formData.finished_goods];
            const totalQty = updatedFG.reduce((sum, item) => sum + (item.quantity_produced || 0), 0);
            const unitCost = totalQty > 0 && totalCost > 0 ? totalCost / totalQty : 0;
            // Use rmSellingPrice state (set when raw material was selected) — avoids stale closure issue
            const rmSellPrice = rmSellingPrice || parseFloat(formData.raw_materials[0]?.selling_price) || 0;
            updatedFG[index] = {
                ...updatedFG[index],
                product_id: product.id,
                product_name: product.product_name || product.name,
                sku: product.sku || product.item_code,
                unit: product.unit_name || 'PCS',
                unit_cost: unitCost,
                selling_price: rmSellPrice,
                total_cost: rmSellPrice * (updatedFG[index].quantity_produced || 0)
            };
            setFormData(prev => ({ ...prev, finished_goods: updatedFG }));
        }
    };

    const handleRawMaterialQuantityChange = (index, quantity) => {
        const updatedItems = [...formData.raw_materials];
        const qty = parseFloat(quantity) || 0;
        updatedItems[index].quantity_used = qty;
        updatedItems[index].total_cost = updatedItems[index].unit_cost * qty;
        setFormData(prev => ({ ...prev, raw_materials: updatedItems }));
        recalculateFinishedGoods(updatedItems);
        if (errors[`raw_qty_${index}`]) setErrors(prev => ({ ...prev, [`raw_qty_${index}`]: '' }));
    };

    const handleRawMaterialUnitCostChange = (index, cost) => {
        const updatedItems = [...formData.raw_materials];
        const unitCost = parseFloat(cost) || 0;
        updatedItems[index].unit_cost = unitCost;
        updatedItems[index].total_cost = unitCost * updatedItems[index].quantity_used;
        setFormData(prev => ({ ...prev, raw_materials: updatedItems }));
        recalculateFinishedGoods(updatedItems);
    };

    const handleFinishedGoodQuantityChange = (index, quantity) => {
        const updatedFG = [...formData.finished_goods];
        updatedFG[index].quantity_produced = parseFloat(quantity) || 0;

        const totalCost = calcTotalProductionCost();
        const totalQty = updatedFG.reduce((sum, item) => sum + (item.quantity_produced || 0), 0);

        if (totalQty > 0 && totalCost > 0) {
            const costPerUnit = totalCost / totalQty;
            updatedFG.forEach((item, idx) => {
                updatedFG[idx].unit_cost = costPerUnit;
                // Preserve selling_price from raw material; recalc total_cost
                updatedFG[idx].total_cost = (item.selling_price || 0) * (item.quantity_produced || 0);
            });
        } else {
            updatedFG.forEach((item, idx) => { updatedFG[idx].unit_cost = 0; updatedFG[idx].total_cost = 0; });
        }
        setFormData(prev => ({ ...prev, finished_goods: updatedFG }));
        if (errors[`fg_qty_${index}`]) setErrors(prev => ({ ...prev, [`fg_qty_${index}`]: '' }));
    };

    const handleWastageQuantityChange = (index, quantity) => {
        const updatedItems = [...formData.wastage];
        updatedItems[index].wastage_quantity = parseFloat(quantity) || 0;
        setFormData(prev => ({ ...prev, wastage: updatedItems }));
    };

    // ── Validation ──────────────────────────────────────────────────────

    const validateForm = () => {
        const newErrors = {};
        const globalErrorList = [];

        if (!formData.batch_no) { newErrors.batch_no = 'Batch number is required'; globalErrorList.push('Batch number is required'); }
        if (formData.raw_materials.length === 0) { newErrors.raw_materials = 'At least one raw material is required'; globalErrorList.push('At least one raw material is required'); }
        if (formData.finished_goods.length === 0) { newErrors.finished_goods = 'At least one finished good is required'; globalErrorList.push('At least one finished good is required'); }

        formData.raw_materials.forEach((item, i) => {
            if (!item.material_id) { newErrors[`raw_product_${i}`] = 'Raw material is required'; globalErrorList.push(`Row ${i + 1}: Raw material selection is required`); }
            if (item.quantity_used <= 0) { newErrors[`raw_qty_${i}`] = 'Quantity must be greater than 0'; globalErrorList.push(`Row ${i + 1}: Raw material quantity must be > 0`); }
        });

        formData.finished_goods.forEach((item, i) => {
            if (!item.product_id) { newErrors[`fg_product_${i}`] = 'Product is required'; globalErrorList.push(`Row ${i + 1}: Finished product selection is required`); }
            if (item.quantity_produced <= 0) { newErrors[`fg_qty_${i}`] = 'Quantity must be greater than 0'; globalErrorList.push(`Row ${i + 1}: Finished product quantity must be > 0`); }
        });

        setErrors(newErrors);
        setGlobalErrors(globalErrorList);
        return Object.keys(newErrors).length === 0;
    };

    // ── Submit ──────────────────────────────────────────────────────────

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error(globalErrors.length > 0 ? globalErrors.slice(0, 3).join('. ') : 'Please fix the errors in the form');
            return;
        }

        recalculateFinishedGoods();
        setSubmitting(true);
        debugger;
        const totalRawCost = calcRawMaterialCost();
        const totalAdditional = calcAdditionalTotal();
        const totalProductionCost = totalRawCost + totalAdditional;
        const totalFinishedQty = calcTotalFinishedQty();
        const finishedGoodsValue = calcFinishedGoodsValue();
        const profitAmount = finishedGoodsValue - totalProductionCost;
        const costPerPiece = totalFinishedQty > 0 ? totalProductionCost / totalFinishedQty : 0;
        const sellingPricePerPiece = costPerPiece * (1 / 100);

        const dataToSend = {
            production_date: formData.production_date,
            batch_no: formData.batch_no,
            notes: formData.notes,

            // Additional overhead costs
            labour_cost: parseFloat(additionalCosts.labour_cost) || 0,
            electricity_cost: parseFloat(additionalCosts.electricity_cost) || 0,
            packing_cost: parseFloat(additionalCosts.packing_cost) || 0,
            transport_cost: parseFloat(additionalCosts.transport_cost) || 0,

            // Summary fields
            profit_margin: profitMarginPercent,
            total_raw_material_cost: totalRawCost,
            total_additional_cost: totalAdditional,
            total_production_cost: totalProductionCost,   // raw + overhead
            cost_per_piece: costPerPiece,
            selling_price_per_piece: sellingPricePerPiece,
            total_production_value: finishedGoodsValue,    // selling value
            profit_amount: profitAmount,

            raw_materials: formData.raw_materials.map(item => ({
                material_id: item.material_id,
                quantity_used: item.quantity_used,
                unit: item.unit,
                unit_cost: item.unit_cost,
                remarks: item.remarks
            })),
            finished_goods: formData.finished_goods.map(item => ({
                product_id: item.product_id,
                quantity_produced: item.quantity_produced,
                unit: item.unit,
                unit_cost: item.unit_cost,
                selling_price: item.selling_price,
                total_cost: item.total_cost,
                remarks: item.remarks
            })),
            wastage: formData.wastage
                .filter(item => item.wastage_quantity > 0)
                .map(item => ({
                    product_id: item.product_id,
                    product_name: item.product_name,
                    wastage_quantity: item.wastage_quantity,
                    unit: item.unit,
                    wastage_type: item.wastage_type,
                    reason: item.reason,
                    wastage_cost: item.wastage_cost,
                    remarks: item.remarks
                }))
        };

        try {
            const result = await manufacturingActions.createProduction(dataToSend);
            if (result.success) {
                toast.success(result.message || 'Production entry created successfully!');
                resetForm();
                setShowCreateModal(false);
                await loadProductions();
                await loadProducts();
                await loadRawMaterials();
                //await loadFinishedGoodsMaterials();
            } else {
                toast.error(result.message || 'Failed to create production');
            }
        } catch (error) {
            console.error('Error creating production:', error);
            toast.error(error.response?.data?.message || error.message || 'Failed to create production');
        } finally {
            setSubmitting(false);
        }
    };

    // ── View ────────────────────────────────────────────────────────────

    const handleViewProduction = async (production) => {
        try {
            const result = await manufacturingActions.getProduction(production.id);
            debugger;
            if (result.success) { setSelectedProduction(result.data); setShowViewModal(true); }
            else toast.error(result.message || 'Failed to load production details');
        } catch (error) {
            console.error('Error loading production:', error);
            toast.error('Failed to load production details');
        }
    };

    // ── Formatters ──────────────────────────────────────────────────────

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null) return '₹0.00';
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount);
    };

    const getStatusBadge = (status) => {
        const cls = status === 'completed' ? 'bg-success' : status === 'draft' ? 'bg-warning' : 'bg-danger';
        return <span className={`badge ${cls}`}>{status?.toUpperCase() || 'N/A'}</span>;
    };

    // ── Options ─────────────────────────────────────────────────────────

    const rawMaterialOptions = (Array.isArray(rawMaterials) ? rawMaterials : []).map(m => ({
        value: m.id,
        label: `${m.material_name} (${m.sku}) - Stock: ${m.quantity || 0} ${m.unit_name || 'PCS'}`,
        data: m
    }));

    // Finished Goods dropdown — shows inventory Products
    const productOptions = (Array.isArray(products) ? products : []).map(p => ({
        value: p.id,
        label: `${p.product_name || p.name} (${p.sku || p.item_code})`
    }));

    const wastageTypeOptions = [
        { value: 'raw_material', label: 'Raw Material' },
        { value: 'finished_good', label: 'Finished Good' }
    ];

    const selectStyles = {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        menu: (base) => ({ ...base, zIndex: 9999 }),
        control: (base) => ({ ...base, minWidth: '200px' })
    };

    // ── Live totals ─────────────────────────────────────────────────────

    const totalRawCost = calcRawMaterialCost();
    const totalAdditionalCost = calcAdditionalTotal();
    const totalProductionCost = totalRawCost + totalAdditionalCost;
    const totalFinishedGoods = calcTotalFinishedQty();
    const finishedGoodsValue = calcFinishedGoodsValue();
    const profitAmt = finishedGoodsValue - totalProductionCost;
    const costPerPiece = totalFinishedGoods > 0 ? totalProductionCost / totalFinishedGoods : 0;
    const sellingPricePerPiece = costPerPiece * (1 / 100);

    const totals = {
        totalRawMaterials: formData.raw_materials.reduce((sum, item) => sum + (item.quantity_used || 0), 0),
        totalRawCost,
        totalAdditionalCost,
        totalProductionCost,
        totalFinishedGoods,
        totalFinishedCost: formData.finished_goods.reduce((sum, item) => sum + (item.total_cost || 0), 0),
        totalWastage: formData.wastage.reduce((sum, item) => sum + (item.wastage_quantity || 0), 0),
        finishedGoodsValue,
        profitAmount: profitAmt,
        costPerPiece,
        sellingPricePerPiece
    };

    const renderTooltip = (props, text) => <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>;

    // ═══════════════════════════════════════════════════════════════════
    return (
        <div className="page-wrapper">
            <div className="content">

                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Production Entry</h4>
                        <h6>Record manufacturing production and track output</h6>
                    </div>
                    <ul className="table-top-head">
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <Link onClick={() => {
                                    loadProductions();
                                    loadProducts();
                                    loadRawMaterials();
                                    //loadFinishedGoodsMaterials(); 
                                }}>
                                    <RotateCcw /></Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <Link className={data ? "active" : ""} onClick={() => dispatch(setToogleHeader(!data))}><ChevronUp /></Link>
                            </OverlayTrigger>
                        </li>
                    </ul>
                    {canCreate && (
                        <div className="page-btn">
                            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                                <Plus size={18} className="me-2" />New Production
                            </button>
                        </div>
                    )}
                </div>

                {/* Stats Cards */}
                <div className="row">
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card"><div className="card-body"><div className="d-flex align-items-center">
                            <div className="bg-primary bg-opacity-10 p-3 rounded me-3"><Settings size={24} className="text-primary" /></div>
                            <div><p className="text-muted mb-1">Total Productions</p><h3 className="mb-0">{pagination.total}</h3></div>
                        </div></div></div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card"><div className="card-body"><div className="d-flex align-items-center">
                            <div className="bg-success bg-opacity-10 p-3 rounded me-3"><CheckCircle size={24} className="text-success" /></div>
                            <div><p className="text-muted mb-1">Completed</p><h3 className="mb-0">{productions.filter(p => p.status === 'completed').length}</h3></div>
                        </div></div></div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card"><div className="card-body"><div className="d-flex align-items-center">
                            <div className="bg-warning bg-opacity-10 p-3 rounded me-3"><TrendingUp size={24} className="text-warning" /></div>
                            <div><p className="text-muted mb-1">Total Units Produced</p><h3 className="mb-0">{productions.reduce((sum, p) => sum + (parseInt(p.total_quantity) || 0), 0).toLocaleString()}</h3></div>
                        </div></div></div>
                    </div>
                    <div className="col-lg-3 col-md-6 col-sm-12">
                        <div className="card"><div className="card-body"><div className="d-flex align-items-center">
                            <div className="bg-info bg-opacity-10 p-3 rounded me-3"><DollarSign size={24} className="text-info" /></div>
                            <div>
                                <p className="text-muted mb-1">Total Production Value</p>
                                {/* FIXED: reads total_production_value (with profit) not just cost */}
                                <h3 className="mb-0">{formatCurrency(productions.reduce((sum, p) => sum + (parseFloat(p.total_production_value) || 0), 0))}</h3>
                            </div>
                        </div></div></div>
                    </div>
                </div>

                {/* Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-4">
                                <div className="mb-3">
                                    <label>Search</label>
                                    <div className="input-group">
                                        <input type="text" className="form-control" placeholder="Batch no, production no..."
                                            value={filters.search} onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                            onKeyPress={(e) => e.key === 'Enter' && loadProductions(1)} />
                                        <button className="btn btn-primary" onClick={() => loadProductions(1)}><Search size={16} /></button>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3">
                                <div className="mb-3">
                                    <label>Status</label>
                                    <Select options={statusOptions} isClearable placeholder="All Status"
                                        onChange={(selected) => { setFilters(prev => ({ ...prev, status: selected?.value || '' })); loadProductions(1); }} />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>From Date</label>
                                    <input type="date" className="form-control" value={filters.from_date}
                                        onChange={(e) => { setFilters(prev => ({ ...prev, from_date: e.target.value })); loadProductions(1); }} />
                                </div>
                            </div>
                            <div className="col-lg-2">
                                <div className="mb-3">
                                    <label>To Date</label>
                                    <input type="date" className="form-control" value={filters.to_date}
                                        onChange={(e) => { setFilters(prev => ({ ...prev, to_date: e.target.value })); loadProductions(1); }} />
                                </div>
                            </div>
                            <div className="col-lg-1">
                                <div className="mb-3">
                                    <label>&nbsp;</label>
                                    <button className="btn btn-secondary w-100"
                                        onClick={() => { setFilters({ search: '', status: '', from_date: '', to_date: '' }); loadProductions(1); }}>
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Productions Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                            </div>
                        ) : productions.length === 0 ? (
                            <div className="text-center py-5">
                                <Settings size={48} className="text-muted mb-3" />
                                <h5>No productions found</h5>
                                <p className="text-muted">Create your first production entry to get started</p>
                                {canCreate && (
                                    <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                                        <Plus size={16} className="me-2" />New Production
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th width="40px">
                                                <input type="checkbox" className="form-check-input"
                                                    checked={selectedRows.length === productions.length && productions.length > 0}
                                                    onChange={(e) => { if (e.target.checked) setSelectedRows(productions.map(p => p.id)); else setSelectedRows([]); }} />
                                            </th>
                                            <th>Production No</th>
                                            <th>Batch No</th>
                                            <th>Date</th>
                                            <th>Raw Materials</th>
                                            <th>Finished Goods</th>
                                            <th>Total Cost</th>
                                            {/* FIXED: separate columns for cost vs value vs profit */}
                                            <th>Production Value</th>
                                            <th>Profit</th>
                                            <th>Status</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productions.map((production) => (
                                            <tr key={production.id}>
                                                <td>
                                                    <input type="checkbox" className="form-check-input"
                                                        checked={selectedRows.includes(production.id)}
                                                        onChange={(e) => { if (e.target.checked) setSelectedRows([...selectedRows, production.id]); else setSelectedRows(selectedRows.filter(id => id !== production.id)); }} />
                                                </td>
                                                <td><strong>{production.production_no}</strong></td>
                                                <td><code>{production.batch_no}</code></td>
                                                <td><div className="d-flex align-items-center"><Calendar size={14} className="me-1 text-muted" />{formatDate(production.production_date)}</div></td>
                                                <td>{production.raw_materials_count || 0} items</td>
                                                <td>{production.finished_goods_count || 0} items</td>
                                                <td className="text-warning fw-bold">{formatCurrency(production.total_production_cost)}</td>
                                                <td className="text-success fw-bold">{formatCurrency(production.total_production_value)}</td>
                                                {/* Profit = value - cost */}
                                                <td className="text-info">{formatCurrency(production.profit_amount)}</td>
                                                <td>{getStatusBadge(production.status)}</td>
                                                <td className="text-center">
                                                    <button className="btn btn-sm btn-outline-info me-1" onClick={() => handleViewProduction(production)} title="View Details"><Eye size={14} /></button>
                                                    {canEdit && production.status === 'draft' && (
                                                        <button className="btn btn-sm btn-outline-primary" title="Edit"><Edit size={14} /></button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <div className="text-muted">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                </div>
                                <nav>
                                    <ul className="pagination">
                                        <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => loadProductions(pagination.page - 1)}>Previous</button>
                                        </li>
                                        {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                            let pageNum = i + 1;
                                            if (pagination.totalPages > 5) {
                                                if (pagination.page <= 3) pageNum = i + 1;
                                                else if (pagination.page >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
                                                else pageNum = pagination.page - 2 + i;
                                            }
                                            return (
                                                <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                                                    <button className="page-link" onClick={() => loadProductions(pageNum)}>{pageNum}</button>
                                                </li>
                                            );
                                        })}
                                        <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => loadProductions(pagination.page + 1)}>Next</button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ═══════════════════════════════════════════════════════
                CREATE PRODUCTION MODAL
            ═══════════════════════════════════════════════════════ */}
            {showCreateModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title"><Settings size={18} className="me-2" />New Production Entry</h5>
                                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                            </div>
                            <div className="modal-body">

                                {/* Global Errors */}
                                {globalErrors.length > 0 && (
                                    <div className="alert alert-danger alert-dismissible fade show mb-3" role="alert">
                                        <strong>Please fix the following errors:</strong>
                                        <ul className="mb-0 mt-1">
                                            {globalErrors.slice(0, 5).map((err, idx) => <li key={idx}>{err}</li>)}
                                            {globalErrors.length > 5 && <li>...and {globalErrors.length - 5} more errors</li>}
                                        </ul>
                                        <button type="button" className="btn-close" onClick={() => setGlobalErrors([])}></button>
                                    </div>
                                )}

                                {/* Basic Info */}
                                <div className="row mb-4">
                                    <div className="col-md-4">
                                        <label className="form-label">Production Date *</label>
                                        <input type="date" className="form-control" value={formData.production_date}
                                            onChange={(e) => setFormData(prev => ({ ...prev, production_date: e.target.value }))} required />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Batch Number *</label>
                                        <div className="input-group">
                                            <input type="text" className={`form-control ${errors.batch_no ? 'is-invalid' : ''}`}
                                                value={formData.batch_no}
                                                onChange={(e) => setFormData(prev => ({ ...prev, batch_no: e.target.value }))}
                                                placeholder="Enter batch number" />
                                            <button type="button" className="btn btn-outline-secondary" onClick={generateNewBatchNo} title="Generate new">
                                                <RotateCcw size={16} />
                                            </button>
                                        </div>
                                        {errors.batch_no && <div className="text-danger small mt-1">{errors.batch_no}</div>}
                                    </div>
                                    <div className="col-md-12 mt-3">
                                        <label className="form-label">Notes</label>
                                        <textarea className="form-control" value={formData.notes}
                                            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                            rows="2" placeholder="Production notes, batch details, etc." />
                                    </div>
                                </div>

                                {/* ── ADDITIONAL COSTS CARD ──────────────────────────────── */}
                                <div className="card border-warning mb-4">
                                    <div className="card-header bg-warning bg-opacity-10 d-flex align-items-center">
                                        <DollarSign size={16} className="me-2 text-warning" />
                                        <h6 className="mb-0">Additional Production Costs (Overhead)</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="row g-3">
                                            {/* Labour */}
                                            <div className="col-md-3">
                                                <label className="form-label">Labour Cost (₹)</label>
                                                <div className="input-group">
                                                    <span className="input-group-text">₹</span>
                                                    <input type="number" className="form-control"
                                                        value={additionalCosts.labour_cost || ''}
                                                        onChange={(e) => { handleAdditionalCostChange('labour_cost', e.target.value); recalculateFinishedGoods(); }}
                                                        step="0.01" min="0" placeholder="0.00" />
                                                </div>
                                                <small className="text-muted">Wages / salaries for this batch</small>
                                            </div>
                                            {/* Electricity */}
                                            <div className="col-md-3">
                                                <label className="form-label">Electricity Cost (₹)</label>
                                                <div className="input-group">
                                                    <span className="input-group-text">₹</span>
                                                    <input type="number" className="form-control"
                                                        value={additionalCosts.electricity_cost || ''}
                                                        onChange={(e) => { handleAdditionalCostChange('electricity_cost', e.target.value); recalculateFinishedGoods(); }}
                                                        step="0.01" min="0" placeholder="0.00" />
                                                </div>
                                                <small className="text-muted">Power / utilities</small>
                                            </div>
                                            {/* Packing */}
                                            <div className="col-md-3">
                                                <label className="form-label">Packing Cost (₹)</label>
                                                <div className="input-group">
                                                    <span className="input-group-text">₹</span>
                                                    <input type="number" className="form-control"
                                                        value={additionalCosts.packing_cost || ''}
                                                        onChange={(e) => { handleAdditionalCostChange('packing_cost', e.target.value); recalculateFinishedGoods(); }}
                                                        step="0.01" min="0" placeholder="0.00" />
                                                </div>
                                                <small className="text-muted">Packaging materials</small>
                                            </div>
                                            {/* Transport */}
                                            <div className="col-md-3">
                                                <label className="form-label">Transport Cost (₹)</label>
                                                <div className="input-group">
                                                    <span className="input-group-text">₹</span>
                                                    <input type="number" className="form-control"
                                                        value={additionalCosts.transport_cost || ''}
                                                        onChange={(e) => { handleAdditionalCostChange('transport_cost', e.target.value); recalculateFinishedGoods(); }}
                                                        step="0.01" min="0" placeholder="0.00" />
                                                </div>
                                                <small className="text-muted">Freight / delivery</small>
                                            </div>
                                        </div>

                                        {/* Additional cost summary bar */}
                                        {totals.totalAdditionalCost > 0 && (
                                            <div className="mt-3 p-2 bg-light rounded d-flex gap-3 flex-wrap small align-items-center">
                                                <span><strong>Labour:</strong> {formatCurrency(additionalCosts.labour_cost)}</span>
                                                <span><strong>Electricity:</strong> {formatCurrency(additionalCosts.electricity_cost)}</span>
                                                <span><strong>Packing:</strong> {formatCurrency(additionalCosts.packing_cost)}</span>
                                                <span><strong>Transport:</strong> {formatCurrency(additionalCosts.transport_cost)}</span>
                                                <span className="ms-auto fw-bold text-warning">
                                                    Total Overhead: {formatCurrency(totals.totalAdditionalCost)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* ──────────────────────────────────────────────────────── */}

                                {/* Tabs */}
                                <ul className="nav nav-tabs mb-3">
                                    <li className="nav-item">
                                        <button className={`nav-link ${activeTab === 'raw_materials' ? 'active' : ''}`} onClick={() => setActiveTab('raw_materials')}>
                                            <Package size={14} className="me-1" />
                                            Raw Materials ({formData.raw_materials.length})
                                            {errors.raw_materials && <span className="ms-1 text-danger">*</span>}
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button className={`nav-link ${activeTab === 'finished_goods' ? 'active' : ''}`} onClick={() => setActiveTab('finished_goods')}>
                                            <CheckCircle size={14} className="me-1" />
                                            Finished Goods ({formData.finished_goods.length})
                                            {errors.finished_goods && <span className="ms-1 text-danger">*</span>}
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button className={`nav-link ${activeTab === 'wastage' ? 'active' : ''}`} onClick={() => setActiveTab('wastage')}>
                                            <AlertCircle size={14} className="me-1" />
                                            Wastage ({formData.wastage.length})
                                        </button>
                                    </li>
                                </ul>

                                {/* ── RAW MATERIALS TAB ──────────────────────────────────── */}
                                {activeTab === 'raw_materials' && (
                                    <div>
                                        <div className="d-flex justify-content-end mb-3">
                                            <button type="button" className="btn btn-sm btn-primary" onClick={handleAddRawMaterial}>
                                                <Plus size={14} className="me-1" /> Add Raw Material
                                            </button>
                                        </div>
                                        <div className="table-responsive">
                                            <table className="table table-bordered">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th style={{ width: '30%' }}>Raw Material</th>
                                                        <th style={{ width: '15%' }}>Qty Used</th>
                                                        <th style={{ width: '10%' }}>Unit</th>
                                                        <th style={{ width: '20%' }}>Unit Cost (₹)</th>
                                                        <th style={{ width: '20%' }}>Total Cost (₹)</th>
                                                        <th style={{ width: '5%' }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {formData.raw_materials.length === 0 ? (
                                                        <tr><td colSpan="6" className="text-center py-4 text-muted">Click Add Raw Material to add materials</td></tr>
                                                    ) : formData.raw_materials.map((item, index) => (
                                                        <tr key={item.id}>
                                                            <td>
                                                                <Select options={rawMaterialOptions}
                                                                    value={rawMaterialOptions.find(opt => opt.value === item.material_id)}
                                                                    onChange={(sel) => handleRawMaterialSelect(index, sel)}
                                                                    placeholder="Select raw material"
                                                                    className={errors[`raw_product_${index}`] ? 'is-invalid' : ''}
                                                                    menuPortalTarget={document.body} styles={selectStyles} />
                                                                {errors[`raw_product_${index}`] && <div className="text-danger small mt-1">{errors[`raw_product_${index}`]}</div>}
                                                            </td>
                                                            <td>
                                                                <input type="number" className={`form-control ${errors[`raw_qty_${index}`] ? 'is-invalid' : ''}`}
                                                                    value={item.quantity_used || ''}
                                                                    onChange={(e) => handleRawMaterialQuantityChange(index, e.target.value)}
                                                                    step="0.01" min="0" placeholder="Qty" />
                                                                {errors[`raw_qty_${index}`] && <div className="text-danger small mt-1">{errors[`raw_qty_${index}`]}</div>}
                                                            </td>
                                                            <td><input type="text" className="form-control" value={item.unit} readOnly /></td>
                                                            <td>
                                                                <input type="number" className="form-control" value={item.unit_cost || ''}
                                                                    onChange={(e) => handleRawMaterialUnitCostChange(index, e.target.value)}
                                                                    step="0.01" min="0" placeholder="Cost" />
                                                            </td>
                                                            <td>
                                                                <input type="text" className="form-control bg-light fw-bold text-primary"
                                                                    value={formatCurrency(item.total_cost)} readOnly />
                                                            </td>
                                                            <td className="text-center">
                                                                <button type="button" className="btn btn-sm btn-danger" onClick={() => handleRemoveItem('raw_materials', index)}>
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                {formData.raw_materials.length > 0 && (
                                                    <tfoot className="table-light">
                                                        <tr>
                                                            <td colSpan="3" className="text-end fw-bold">Totals:</td>
                                                            <td className="fw-bold">{totals.totalRawMaterials.toFixed(2)} Units</td>
                                                            <td className="fw-bold text-primary">{formatCurrency(totals.totalRawCost)}</td>
                                                            <td></td>
                                                        </tr>
                                                    </tfoot>
                                                )}
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* ── FINISHED GOODS TAB ─────────────────────────────────── */}
                                {activeTab === 'finished_goods' && (
                                    <div>
                                        <div className="d-flex justify-content-end mb-3">
                                            <button type="button" className="btn btn-sm btn-primary" onClick={handleAddFinishedGood}>
                                                <Plus size={14} className="me-1" /> Add Finished Good
                                            </button>
                                        </div>
                                        {loadingProducts ? (
                                            <div className="text-center py-3">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                                                <span className="ms-2">Loading products...</span>
                                            </div>
                                        ) : productOptions.length === 0 ? (
                                            <div className="alert alert-warning">
                                                <AlertCircle size={16} className="me-2" />No active products found. Please add products in the Product module first.
                                            </div>
                                        ) : null}
                                        <div className="table-responsive">
                                            <table className="table table-bordered">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th style={{ width: '28%' }}>Finished Product</th>
                                                        <th style={{ width: '12%' }}>Qty Produced</th>
                                                        <th style={{ width: '8%' }}>Unit</th>
                                                        <th style={{ width: '15%' }}>Cost/Unit (₹)</th>
                                                        <th style={{ width: '15%' }}>Sell Price/Unit (₹)</th>
                                                        <th style={{ width: '15%' }}>Total Value (₹)</th>
                                                        <th style={{ width: '7%' }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {formData.finished_goods.length === 0 ? (
                                                        <tr><td colSpan="7" className="text-center py-4 text-muted">Click Add Finished Good to add products</td></tr>
                                                    ) : formData.finished_goods.map((item, index) => (
                                                        <tr key={item.id}>
                                                            <td>
                                                                <Select options={productOptions}
                                                                    value={productOptions.find(opt => opt.value === item.product_id)}
                                                                    onChange={(sel) => handleFinishedGoodSelect(index, sel)}
                                                                    placeholder="Select finished product"
                                                                    className={errors[`fg_product_${index}`] ? 'is-invalid' : ''}
                                                                    menuPortalTarget={document.body} styles={selectStyles}
                                                                    isDisabled={productOptions.length === 0} />
                                                                {errors[`fg_product_${index}`] && <div className="text-danger small mt-1">{errors[`fg_product_${index}`]}</div>}
                                                            </td>
                                                            <td>
                                                                <input type="number" className={`form-control ${errors[`fg_qty_${index}`] ? 'is-invalid' : ''}`}
                                                                    value={item.quantity_produced || ''}
                                                                    onChange={(e) => handleFinishedGoodQuantityChange(index, e.target.value)}
                                                                    step="0.01" min="0" placeholder="Qty" />
                                                                {errors[`fg_qty_${index}`] && <div className="text-danger small mt-1">{errors[`fg_qty_${index}`]}</div>}
                                                            </td>
                                                            <td><input type="text" className="form-control" value={item.unit} readOnly /></td>
                                                            {/* Cost per unit = total production cost / total qty */}
                                                            <td>
                                                                <input type="text" className="form-control bg-light text-muted"
                                                                    value={item.unit_cost ? item.unit_cost.toFixed(4) : '0.0000'} readOnly />
                                                            </td>
                                                            {/* Selling price per unit (includes profit margin) */}
                                                            <td>
                                                                <input type="text" className="form-control bg-light fw-bold text-success"
                                                                    value={item.selling_price ? item.selling_price.toFixed(4) : '0.0000'} readOnly />
                                                            </td>
                                                            {/* Total = selling_price × qty */}
                                                            <td>
                                                                <input type="text" className="form-control bg-light fw-bold text-primary"
                                                                    value={formatCurrency(item.total_cost)} readOnly />
                                                            </td>
                                                            <td className="text-center">
                                                                <button type="button" className="btn btn-sm btn-danger" onClick={() => handleRemoveItem('finished_goods', index)}>
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                {formData.finished_goods.length > 0 && (
                                                    <tfoot className="table-light">
                                                        <tr>
                                                            <td colSpan="2" className="text-end fw-bold">Totals:</td>
                                                            <td className="fw-bold">{totals.totalFinishedGoods.toFixed(2)} Units</td>
                                                            <td></td><td></td>
                                                            <td className="fw-bold text-primary">{formatCurrency(totals.totalFinishedCost)}</td>
                                                            <td></td>
                                                        </tr>
                                                    </tfoot>
                                                )}
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* ── WASTAGE TAB ────────────────────────────────────────── */}
                                {activeTab === 'wastage' && (
                                    <div>
                                        <div className="d-flex justify-content-end mb-3">
                                            <button type="button" className="btn btn-sm btn-warning" onClick={handleAddWastage}>
                                                <Plus size={14} className="me-1" /> Add Wastage
                                            </button>
                                        </div>
                                        <div className="table-responsive">
                                            <table className="table table-bordered">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th style={{ width: '25%' }}>Product</th>
                                                        <th style={{ width: '15%' }}>Wastage Quantity</th>
                                                        <th style={{ width: '10%' }}>Unit</th>
                                                        <th style={{ width: '15%' }}>Type</th>
                                                        <th style={{ width: '25%' }}>Reason</th>
                                                        <th style={{ width: '10%' }}>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {formData.wastage.length === 0 ? (
                                                        <tr><td colSpan="6" className="text-center py-4 text-muted">Click Add Wastage to record waste</td></tr>
                                                    ) : formData.wastage.map((item, index) => {
                                                        const optionsForType = item.wastage_type === 'raw_material' ? rawMaterialOptions : productOptions;
                                                        return (
                                                            <tr key={item.id}>
                                                                <td>
                                                                    <Select options={optionsForType}
                                                                        value={optionsForType.find(opt => opt.value === item.product_id)}
                                                                        onChange={(sel) => {
                                                                            const found = item.wastage_type === 'raw_material'
                                                                                ? rawMaterials.find(p => p.id === sel.value)
                                                                                : products.find(p => p.id === sel.value);
                                                                            if (found) {
                                                                                const upd = [...formData.wastage];
                                                                                upd[index] = { ...upd[index], product_id: found.id, product_name: item.wastage_type === 'raw_material' ? found.material_name : (found.product_name || found.name), sku: found.sku || found.item_code, unit: found.unit_name || 'PCS' };
                                                                                setFormData(prev => ({ ...prev, wastage: upd }));
                                                                            }
                                                                        }}
                                                                        placeholder={item.wastage_type === 'raw_material' ? "Select raw material" : "Select finished product"}
                                                                        isDisabled={!item.wastage_type}
                                                                        menuPortalTarget={document.body} styles={selectStyles} />
                                                                </td>
                                                                <td>
                                                                    <input type="number" className="form-control"
                                                                        value={item.wastage_quantity || ''}
                                                                        onChange={(e) => handleWastageQuantityChange(index, e.target.value)}
                                                                        step="0.01" min="0" placeholder="Qty" />
                                                                </td>
                                                                <td><input type="text" className="form-control" value={item.unit} readOnly /></td>
                                                                <td>
                                                                    <Select options={wastageTypeOptions}
                                                                        value={wastageTypeOptions.find(opt => opt.value === item.wastage_type)}
                                                                        onChange={(sel) => {
                                                                            const upd = [...formData.wastage];
                                                                            upd[index] = { ...upd[index], wastage_type: sel?.value || 'raw_material', product_id: '', product_name: '', sku: '', unit: 'PCS' };
                                                                            setFormData(prev => ({ ...prev, wastage: upd }));
                                                                        }}
                                                                        placeholder="Select type"
                                                                        menuPortalTarget={document.body} styles={selectStyles} />
                                                                </td>
                                                                <td>
                                                                    <input type="text" className="form-control" value={item.reason || ''}
                                                                        onChange={(e) => {
                                                                            const upd = [...formData.wastage];
                                                                            upd[index].reason = e.target.value;
                                                                            setFormData(prev => ({ ...prev, wastage: upd }));
                                                                        }}
                                                                        placeholder="Reason for wastage" />
                                                                </td>
                                                                <td className="text-center">
                                                                    <button type="button" className="btn btn-sm btn-danger" onClick={() => handleRemoveItem('wastage', index)}>
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                                {formData.wastage.length > 0 && (
                                                    <tfoot className="table-light">
                                                        <tr>
                                                            <td colSpan="2" className="text-end fw-bold">Total Wastage:</td>
                                                            <td className="fw-bold">{totals.totalWastage.toFixed(2)} Units</td>
                                                            <td colSpan="3"></td>
                                                        </tr>
                                                    </tfoot>
                                                )}
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* ── COST SUMMARY ──────────────────────────────────────── */}
                                <div className="border-top pt-3 mt-3">
                                    <h6 className="fw-bold mb-3 text-secondary">
                                        <TrendingUp size={16} className="me-2" />Production Cost Summary
                                    </h6>

                                    {/* Cost breakdown mini cards */}
                                    <div className="row g-2 mb-3">
                                        {[
                                            { label: 'Raw Material', value: totals.totalRawCost, cls: 'text-primary' },
                                            { label: 'Labour', value: additionalCosts.labour_cost, cls: '' },
                                            { label: 'Electricity', value: additionalCosts.electricity_cost, cls: '' },
                                            { label: 'Packing', value: additionalCosts.packing_cost, cls: '' },
                                            { label: 'Transport', value: additionalCosts.transport_cost, cls: '' },
                                            { label: 'Total Cost', value: totals.totalProductionCost, cls: 'text-warning fw-bold', highlight: true },
                                        ].map(({ label, value, cls, highlight }) => (
                                            <div key={label} className="col-md-2">
                                                <div className={`card h-100 text-center ${highlight ? 'bg-warning bg-opacity-10' : 'bg-light'}`}>
                                                    <div className="card-body py-2">
                                                        <small className="text-muted d-block">{label}</small>
                                                        <strong className={cls}>{formatCurrency(value)}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Main summary cards */}
                                    <div className="row g-3">
                                        <div className="col-md-4">
                                            <div className="card bg-light">
                                                <div className="card-body text-center">
                                                    <h6>Total Production Cost</h6>
                                                    <h4 className="text-warning">{formatCurrency(totals.totalProductionCost)}</h4>
                                                    <small className="text-muted">Raw + Labour + Electricity + Packing + Transport</small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="card bg-light">
                                                <div className="card-body text-center">
                                                    <h6>Finished Goods Value</h6>
                                                    <h4 className="text-success">{formatCurrency(totals.finishedGoodsValue)}</h4>
                                                    <small className="text-muted">Qty &times; Selling Price (from RM entry)</small>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="card bg-light">
                                                <div className="card-body text-center">
                                                    <h6>Profit Amount</h6>
                                                    <h4 className={totals.profitAmount >= 0 ? "text-info" : "text-danger"}>
                                                        {formatCurrency(totals.profitAmount)}
                                                    </h4>
                                                    <small className="text-muted">Value - Total Production Cost</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>


                                </div>
                                {/* ──────────────────────────────────────────────────────── */}

                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Cancel</button>
                                <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
                                    {submitting ? (
                                        <><span className="spinner-border spinner-border-sm me-2"></span>Processing...</>
                                    ) : (
                                        <><CheckCircle size={16} className="me-2" />Save Production</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════
                VIEW PRODUCTION MODAL
            ═══════════════════════════════════════════════════════ */}
            {showViewModal && selectedProduction && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title"><Settings size={18} className="me-2" />Production Details - {selectedProduction.production_no}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowViewModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row mb-4">
                                    <div className="col-md-6">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <h6 className="mb-3">Production Information</h6>
                                                <p><strong>Production No:</strong> {selectedProduction.production_no}</p>
                                                <p><strong>Batch No:</strong> <code>{selectedProduction.batch_no}</code></p>
                                                <p><strong>Date:</strong> {formatDate(selectedProduction.production_date)}</p>
                                                <p><strong>Status:</strong> {getStatusBadge(selectedProduction.status)}</p>
                                                {selectedProduction.notes && <p><strong>Notes:</strong> {selectedProduction.notes}</p>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <h6 className="mb-3">Cost Summary</h6>
                                                <p><strong>Raw Materials Cost:</strong> {formatCurrency(selectedProduction.total_raw_material_cost)}</p>
                                                {selectedProduction.labour_cost > 0 && <p><strong>Labour Cost:</strong> {formatCurrency(selectedProduction.labour_cost)}</p>}
                                                {selectedProduction.electricity_cost > 0 && <p><strong>Electricity Cost:</strong> {formatCurrency(selectedProduction.electricity_cost)}</p>}
                                                {selectedProduction.packing_cost > 0 && <p><strong>Packing Cost:</strong> {formatCurrency(selectedProduction.packing_cost)}</p>}
                                                {selectedProduction.transport_cost > 0 && <p><strong>Transport Cost:</strong> {formatCurrency(selectedProduction.transport_cost)}</p>}
                                                <p className="border-top pt-2">
                                                    <strong>Total Production Cost:</strong>{' '}
                                                    <span className="text-warning fw-bold">{formatCurrency(selectedProduction.total_production_cost)}</span>
                                                </p>
                                                <p>
                                                    <strong>Total Quantity Produced:</strong>{' '}
                                                    <span className="fw-bold">{selectedProduction.finished_goods?.reduce((sum, item) => sum + (item.quantity_produced || 0), 0) || 0} units</span>
                                                </p>
                                                <p>
                                                    <strong>Selling Price / Unit:</strong>{' '}
                                                    <span className="text-success fw-bold">{formatCurrency(selectedProduction.selling_price_per_unit || selectedProduction.selling_price_per_piece)}</span>
                                                </p>
                                                <p>
                                                    <strong>Production Value (with profit):</strong>{' '}
                                                    <span className="text-success fw-bold">{formatCurrency(selectedProduction.total_production_value)}</span>
                                                </p>
                                                <p><strong>Profit Amount:</strong> <span className="text-info fw-bold">{formatCurrency(selectedProduction.profit_amount)}</span></p>
                                                <p><strong>Cost / Piece:</strong> {formatCurrency(selectedProduction.cost_per_piece)}</p>
                                                {selectedProduction.total_wastage_cost > 0 && (
                                                    <p><strong>Wastage Cost:</strong> <span className="text-danger">{formatCurrency(selectedProduction.total_wastage_cost)}</span></p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Raw Materials Used */}
                                {selectedProduction.raw_materials?.length > 0 && (
                                    <div className="mb-4">
                                        <h6 className="fw-bold mb-3">Raw Materials Used</h6>
                                        <div className="table-responsive">
                                            <table className="table table-bordered">
                                                <thead className="table-light">
                                                    <tr><th>Product Name</th><th>SKU</th><th>Qty Used</th><th>Unit</th><th>Unit Cost</th><th>Total Cost</th></tr>
                                                </thead>
                                                <tbody>
                                                    {selectedProduction.raw_materials.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.product_name}</td>
                                                            <td><code>{item.sku}</code></td>
                                                            <td>{item.quantity_used}</td>
                                                            <td>{item.unit}</td>
                                                            <td>{formatCurrency(item.unit_cost)}</td>
                                                            <td>{formatCurrency(item.total_cost)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <td colSpan="5" className="text-end fw-bold">Total Raw Material Cost:</td>
                                                        <td className="fw-bold">{formatCurrency(selectedProduction.total_raw_material_cost)}</td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Finished Goods */}
                                {selectedProduction.finished_goods?.length > 0 && (
                                    <div className="mb-4">
                                        <h6 className="fw-bold mb-3">Finished Goods Produced</h6>
                                        <div className="table-responsive">
                                            <table className="table table-bordered">
                                                <thead className="table-light">
                                                    <tr><th>Product Name</th><th>SKU</th><th>Qty Produced</th><th>Unit</th><th>Cost/Unit</th><th>Selling Price/Unit</th><th>Total Value</th></tr>
                                                </thead>
                                                <tbody>
                                                    {selectedProduction.finished_goods.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.product_name}</td>
                                                            <td><code>{item.sku}</code></td>
                                                            <td>{item.quantity_produced}</td>
                                                            <td>{item.unit}</td>
                                                            <td>{formatCurrency(item.unit_cost)}</td>
                                                            <td className="text-success">{formatCurrency(item.selling_price)}</td>
                                                            <td className="fw-bold">{formatCurrency(item.total_cost)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <td colSpan="6" className="text-end fw-bold">Total Production Value:</td>
                                                        <td className="fw-bold text-success">{formatCurrency(selectedProduction.total_production_value)}</td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Wastage */}
                                {selectedProduction.wastage?.length > 0 && (
                                    <div>
                                        <h6 className="fw-bold mb-3">Wastage Recorded</h6>
                                        <div className="table-responsive">
                                            <table className="table table-bordered">
                                                <thead className="table-light">
                                                    <tr><th>Product Name</th><th>SKU</th><th>Wastage Qty</th><th>Unit</th><th>Type</th><th>Reason</th><th>Cost</th></tr>
                                                </thead>
                                                <tbody>
                                                    {selectedProduction.wastage.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.product_name}</td>
                                                            <td><code>{item.sku}</code></td>
                                                            <td>{item.wastage_quantity}</td>
                                                            <td>{item.unit}</td>
                                                            <td><span className="badge bg-warning">{item.wastage_type}</span></td>
                                                            <td>{item.reason || '-'}</td>
                                                            <td>{formatCurrency(item.wastage_cost)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <td colSpan="6" className="text-end fw-bold">Total Wastage Cost:</td>
                                                        <td className="fw-bold text-danger">{formatCurrency(selectedProduction.total_wastage_cost)}</td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductionEntry;
